// Explicit synthetic preview data. Never used as component defaults.
import { ART_BASE } from '../assets.js';
// The ONE versioned legal and claims source, read through the same `~shared` alias the Astro
// marketing site uses. See the note on public claims below.
import legal from '~shared/legal-source.json';

const ASSET = ART_BASE;

/**
 * F-14 (#24). Acceptance: "App and homepage pricing/availability agree; all public quantitative
 * claims have an identified source."
 *
 * `/#/home` is a PUBLIC page. Measured live on dev 2026-09-16: it loads with no sign-in. So the
 * numbers and the price on it are public claims, and the fixture banner does not cover them. The
 * banner marks the demo FAMILY data as not real, which is a different claim from a price or a
 * count, and `Legal.test.jsx` already draws that same line for the legal identity.
 *
 * Measured the same day, this file disagreed with the homepage on every one of them:
 *
 *   app `/#/home`                        homepage and shared/legal-source.json
 *   "500+ families in open beta"         claims.familyCount is null, so the stat is omitted
 *   "25,000+ quests lit so far"          liveClaims.questsCompleted, read live, floor 500
 *   "Open beta · $0"                       "Today · Free · while the app is in development"
 *   "~$5 /mo · coming after beta"        claim('pricingMonthly') · "per month · coming after launch"
 *   "Beta families keep a founders'…"   "Early families keep a founders' discount"
 *
 * Every public number below now comes from the shared source. A `null` claim means the sentence is
 * OMITTED, never a zero, never "many", never an invented figure.
 */
const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const count = new Intl.NumberFormat('en-US');
const asNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const familyCount = asNumber(legal.claims.familyCount);
const monthly = asNumber(legal.claims.pricingMonthly);
/** "development" -> "in development". One word, one source, so the app cannot drift from it. */
const APP_STATUS_PHRASE = `in ${legal.availability.appStatus}`;

export const FIXTURE_PROOF = {
  stats: [
    // `claims.familyCount` is null: the owner chose on 2026-09-06 to REMOVE this claim rather than
    // supply a figure, so the whole stat disappears rather than softening into "hundreds".
    familyCount === null
      ? null
      : { id: 'families', value: count.format(familyCount), label: 'families using ChoreStory' },
    // ponytail: the quest count is OMITTED here rather than read live. The shared source routes it
    // through `liveClaims.questsCompleted` (/api/public/stats, floor 500, no fallback) and the
    // Astro homepage renders it that way. Omission is what F-14 requires of this page — no
    // unsourced number in a public claim. To upgrade, fetch that endpoint and gate the render on
    // `shouldShowLiveClaim` from `~shared/live-claims.js`, which is written and already tested.
    //
    // A third stat, "$0 ever charged for earned rewards", was REMOVED here. An independent
    // reviewer (gpt-5.6-sol, 2026-09-16) called it correctly: it is a public monetary figure with
    // no entry in the shared source, on a page whose own test claims it carries no unsourced
    // number. The PROMISE it made survives verbatim in the pricing foot-note — "a kid's earned
    // money, badges and village progress are never gated behind a paywall" — which is a policy
    // statement rather than a figure, so nothing honest was lost with it.
  ].filter(Boolean),
  quote:
    '“Our six-year-old now asks if he can light the dishes lantern. We came for a chore chart. We stayed ' +
    'because Friday turned into a family holiday.”',
  // "beta parent" stated an availability the product does not have. The name stays synthetic demo
  // content, which the fixture banner does cover; the availability word does not get to hide there.
  attribution: '— Priya, parent of three · week 14 of her village',
};

export const FIXTURE_PLANS = [
  {
    id: 'today',
    title: 'Today',
    price: 'Free',
    priceNote: `· everything included while the app is ${APP_STATUS_PHRASE}`,
    features: [
      'Unlimited kids, quests & the living village',
      'Hold-to-light, streaks & Trophy Room',
      'Family Payday with Save / Share / Spend jars',
    ],
    // F-29 (#576 o15), OWNER DECISION 2026-09-16: the words stay exactly as the owner wrote them,
    // but they leave the "everything included while the app is in development" list, because
    // neither is in the tree. `shared/deferred-delivery.json` records both as
    // `moved-to-coming-soon`, and `app/test/deferred-delivery.test.js` fails if a claim recorded
    // that way is still offered as included here.
    comingSoon: [
      'AI avatar studio (consent-gated)',
      'Kitchen display mode & parent insights',
    ],
    cta: { label: 'Start your village', href: '#/signup' },
  },
  {
    id: 'family',
    title: 'Family plan',
    // A null price is "Later", never a guessed figure. Same rule, same source, as Pricing.astro.
    price: monthly === null ? 'Later' : money.format(monthly),
    priceNote:
      monthly === null
        ? '· price not set yet · coming after launch'
        : '· per month · coming after launch',
    ghost: true,
    features: [
      'Seasonal village decorations & festivals',
      'Inter-family village visits (opt-in)',
      'Priority support for big households',
      'Early families keep a founders’ discount',
    ],
    badge: 'Not for sale yet, on purpose',
  },
];

export const FIXTURE_FEATURES = [
  {
    id: 'hold',
    span: 'b8',
    title: 'Hold-to-light, the signature gesture',
    body:
      'Completing a chore isn’t a checkbox — it’s a half-second of ceremony. Press, hold, kindle. A ' +
      '30-second “oops, not done” undo keeps it honest, and approvals arrive as a high-five, never paperwork.',
    media: {
      type: 'quest',
      quest: {
        title: 'Unload the dishwasher',
        icon: `${ASSET}/icon-dishes.png`,
        reward: 0.75,
        xp: 15,
        status: 'lit',
      },
    },
  },
  {
    id: 'payday',
    span: 'b4',
    title: 'The Family Payday ritual',
    body:
      'One fixed payday (yours is Friday). Approve the week in a single sweep, count earnings out with ' +
      'ceremony, and let the village throw the fireworks.',
    media: {
      type: 'image',
      src: `${ASSET}/payday-festival.jpg`,
      alt: 'A night festival with fireworks over the village',
    },
  },
  {
    id: 'streaks',
    // F-29 (#576 o15): the feature is not in the tree, so the tile says so on its face.
    comingSoon: true,
    span: 'b4',
    title: 'Streaks that forgive',
    titleIcon: 'streak',
    body:
      'Freezes are earned by doing chores, weekend grace is a family setting, and a missed evening dims ' +
      'the lantern — it never “kills” anything. No death imagery, no countdown scarcity, no pressure pings to kids.',
    media: { type: 'chip', tone: 'ember', label: '3 evenings glowing ✦' },
  },
  {
    id: 'village',
    span: 'b8',
    title: 'A living village, not a leaderboard',
    body:
      'Your family’s effort builds a low-poly storybook village in 3D — houses, gardens and lanterns ' +
      'mapped to real stats. It only ever grows: kindness-inverted gamification, cosmetic and additive, ' +
      'with live lights as quests complete.',
    media: { type: 'image', src: `${ASSET}/village-hero.jpg`, alt: 'The 3D family village at dusk, lanterns glowing' },
  },
  {
    id: 'avatars',
    // F-29 (#576 o15): the feature is not in the tree, so the tile says so on its face.
    comingSoon: true,
    span: 'b6',
    title: 'Every kid is the hero of the story',
    body:
      'Our AI avatar studio paints each child as a storybook hero — parent-initiated and consent-gated, ' +
      'because kids’ photos deserve guardrails. Avatars ride on quest cards, trophies and the village gate.',
    media: {
      type: 'avatars',
      faces: [
        { src: `${ASSET}/avatar-kid-1.jpg`, alt: 'Maya’s painted storybook avatar' },
        { src: `${ASSET}/avatar-kid-2.jpg`, alt: 'Leo’s painted storybook avatar' },
        { src: `${ASSET}/avatar-kid-3.jpg`, alt: 'Finn’s painted storybook avatar' },
      ],
    },
  },
  {
    id: 'insights',
    // F-29 (#576 o15): marked coming soon by the owner's 2026-09-16 decision; the feature has since
    // shipped (#583 N1 S12). S15a: the example sentence says what Insights shows, not a cause (D-60).
    comingSoon: true,
    span: 'b6',
    title: 'Insights for parents, in plain language',
    body:
      '“Maya: 12 approvals, Sep 1 to 28.” What happened, never a guess at why: calm charts with direct labels, ' +
      'a table under each, an as-of date and no ranking — plus CSV export when you want the raw ledger.',
    media: { type: 'image', src: `${ASSET}/parent-calm.jpg`, alt: 'A calm, lamp-lit desk with a family ledger' },
  },
];

export const homeFixture = {
  features: FIXTURE_FEATURES,
  proof: FIXTURE_PROOF,
  plans: FIXTURE_PLANS,
};
